import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  SearchIcon,
  CircleX,
  Upload,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import ProductSection from "../ProductsModule/Components/ProductSection";
import MemoizedTextField from "./Components/MemoizedTextField";
import {
  useFetchAllBrandsQuery,
  useLazyFetchPincodeDetailsQuery,
  useLazyFetchPincodeDetailsDeliveryQuery,
  useLazyFetchVendorStoreQuery,
  useCreateCustomerMutation,
  useUploadCustomerDocumentsMutation,
} from "../../../services/baseQuery";
import BrandCheckbox from "./Components/BrandCheckbox";
import { debounce, set } from "lodash";
import { errorToast, successToast } from "../../../services/toast.service";

const taxPayerTypeoptions = ["Regular", "Composition"];
const storeTypeOptions = ["Wholesaler", "Retailer"];

const initialFormState = {
  firmName: "",
  ownerName: "",
  billPrintName: "",
  regPhoneNumber: "",
  altPhoneNumber: "",
  storeType: "",
  s_pincode: "",
  s_localAddress: "",
  s_locality: "",
  s_city: "",
  s_state: "",
  d_pincode: "",
  d_localAddress: "",
  d_locality: "",
  d_city: "",
  d_state: "",
  gstin: "",
  taxPayerType: "",
  creditLimit: "",
  orderValidationSettings: {
    validationType: "USER_SPECIFIC",
    minOrderQty: "",
    minOrderValue: "",
    validationCondition: "",
  },
  brandVisibility: {
    visibilityType: "USER_SPECIFIC",
    removedBrands: [],
  },
  additionalNotes: "",
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

const validateForm = (values, selectAllBrands, selectedBrands, files) => {
  const errors = {};

  // Required field validation
  if (!values.firmName) errors.firmName = "Firm name is required";
  if (!values.ownerName) errors.ownerName = "Owner name is required";
  if (!values.billPrintName)
    errors.billPrintName = "Bill print name is required";
  if (!values.regPhoneNumber)
    errors.regPhoneNumber = "Phone number is required";
  if (!values.storeType) errors.storeType = "Store type is required";

  // Phone number validation
  if (values.regPhoneNumber && values.regPhoneNumber.length !== 10) {
    errors.regPhoneNumber = "Phone number must be 10 digits";
  }
  if (values.altPhoneNumber && values.altPhoneNumber.length !== 10) {
    errors.altPhoneNumber = "Phone number must be 10 digits";
  }

  // Shop address validation
  if (!values.s_pincode) errors.s_pincode = "Pincode is required";
  if (!values.s_localAddress)
    errors.s_localAddress = "Local address is required";
  if (!values.s_locality) errors.s_locality = "Locality is required";

  // Delivery address validation (if not same as shop address)
  if (!values.d_pincode) errors.d_pincode = "Pincode is required";
  if (!values.d_localAddress)
    errors.d_localAddress = "Local address is required";
  if (!values.d_locality) errors.d_locality = "Locality is required";

  // Business details validation
  if (!values.gstin) errors.gstin = "GSTIN is required";
  if (!values.taxPayerType) errors.taxPayerType = "Tax payer type is required";
  if (!values.creditLimit) errors.creditLimit = "Credit limit is required";

  // Order conditions validation
  if (!values.useDefaultOrderValidationSettings) {
    // Check if validation condition is selected
    if (!values.orderValidationSettings?.validationCondition) {
      errors.moqMovCondition = "Please select MOQ/MOV condition";
    } else {
      // If BOTH is selected, require both MOQ and MOV
      if (values.orderValidationSettings.validationCondition === "BOTH") {
        if (!values.orderValidationSettings.minOrderQty) {
          errors.minOrderQty =
            "MOQ is required when 'Both' condition is selected";
        }
        if (!values.orderValidationSettings.minOrderValue) {
          errors.minOrderValue =
            "MOV is required when 'Both' condition is selected";
        }
      }
      // If ANY_ONE is selected, require at least one of MOQ or MOV
      else if (
        values.orderValidationSettings.validationCondition === "ANY_ONE"
      ) {
        if (
          !values.orderValidationSettings.minOrderQty &&
          !values.orderValidationSettings.minOrderValue
        ) {
          errors.minOrderQty = "Please enter either MOQ or MOV";
          errors.minOrderValue = "Please enter either MOQ or MOV";
        }
      }
    }
  }

  // GSTIN validation
  if (
    values.gstin &&
    !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
      values.gstin,
    )
  ) {
    errors.gstin = "Invalid GSTIN format";
  }

  // Credit limit validation
  if (
    values.creditLimit &&
    (isNaN(values.creditLimit) || Number(values.creditLimit) < 0)
  ) {
    errors.creditLimit = "Credit limit must be a positive number";
  }

  // Brand visibility validation
  if (
    !selectAllBrands && // Check if "Select all brands" is not checked
    (!selectedBrands || selectedBrands.length === 0)
  ) {
    errors.brandVisibility = "Please select at least one brand";
  }

  // File validation
  if (files.gstCertificate) {
    if (!ALLOWED_FILE_TYPES.includes(files.gstCertificate.type)) {
      errors.gstCertificate = "Invalid file type.";
    } else if (files.gstCertificate.size > MAX_FILE_SIZE) {
      errors.gstCertificate = "Max size 5MB allowed";
    }
  }

  if (files.businessPan) {
    if (!ALLOWED_FILE_TYPES.includes(files.businessPan.type)) {
      errors.businessPan = "Invalid file type.";
    } else if (files.businessPan.size > MAX_FILE_SIZE) {
      errors.businessPan = "Max size 5MB allowed";
    }
  }

  return errors;
};

const getFilePreview = file => {
  if (!file) return null;

  if (file.type === "application/pdf") {
    return (
      <div className="flex items-center justify-center w-[100px] h-[100px] bg-gray-50 rounded-lg border border-gray-200">
        <FileText size={40} className="text-gray-400" />
      </div>
    );
  }

  if (file.type.startsWith("image/")) {
    return (
      <div className="w-[100px] h-[100px] rounded-lg border border-gray-200 overflow-hidden">
        <img
          src={URL.createObjectURL(file)}
          alt="Preview"
          className="w-full h-full object-cover"
          onLoad={e => URL.revokeObjectURL(e.target.src)} // Clean up URL after load
        />
      </div>
    );
  }

  return null;
};

const AddCustomer = () => {
  const {
    isLoading: fetchBrandsLoading,
    data: brandsResponseData,
    error: fetchBrandsError,
    isError: isFetchBrandsError,
  } = useFetchAllBrandsQuery();
  const [
    triggerSFetchPincodeQuery,
    {
      isFetching: fetchingSPincode,
      isError: fetchingSPincodeError,
      data: pincodeSDetails,
      error: pincodeSError,
    },
  ] = useLazyFetchPincodeDetailsQuery();
  const [
    triggerDFetchPincodeQuery,
    {
      isFetching: fetchingDPincode,
      isError: fetchingDPincodeError,
      data: pincodeDDetails,
      error: pincodeDError,
    },
  ] = useLazyFetchPincodeDetailsDeliveryQuery();
  const [
    triggerFetchVendorStore,
    { isFetching: fetchingVendorStore, data: vendorStoreData },
  ] = useLazyFetchVendorStoreQuery();
  const [createCustomer, { isLoading: isCreatingCustomer }] =
    useCreateCustomerMutation();
  const [uploadDocuments, { isLoading: isUploadingDocuments }] =
    useUploadCustomerDocumentsMutation();
  const [formValues, setFormValues] = useState({
    firmName: "",
    ownerName: "",
    billPrintName: "",
    regPhoneNumber: "",
    altPhoneNumber: "",
    storeType: "",
    s_pincode: "",
    s_localAddress: "",
    s_locality: "",
    s_city: "",
    s_state: "",
    d_pincode: "",
    d_localAddress: "",
    d_locality: "",
    d_city: "",
    d_state: "",
    gstin: "",
    taxPayerType: "",
    creditLimit: "",
    orderValidationSettings: {
      validationType: "USER_SPECIFIC",
      minOrderQty: "",
      minOrderValue: "",
      validationCondition: "",
    },
    brandVisibility: {
      visibilityType: "USER_SPECIFIC",
      removedBrands: [],
    },
    additionalNotes: "",
  });
  const [errors, setErrors] = useState({});
  const [sLocalityOptions, setSLocalityOptions] = useState([]);
  const [dLocalityOptions, setDLocalityOptions] = useState([]);
  const [sameAsSAddressCheckbox, setSameAsSAddressCheckbox] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectAllBrands, setSelectAllBrands] = useState(false);
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [
    useDefaultOrderValidationSettings,
    setUseDefaultOrderValidationSettings,
  ] = useState(false);
  const [files, setFiles] = useState({
    gstCertificate: null,
    businessPan: null,
  });
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    if (fetchingSPincodeError) {
      setErrors({ ...errors, s_pincode: "Invalid pincode" });
      console.log("locality emptied");
      setSLocalityOptions(prevState => []);
    } else setErrors({ ...errors, s_pincode: undefined });
    if (
      pincodeSDetails?.data &&
      Object.keys(pincodeSDetails?.data).length > 0
    ) {
      const { city_district, state, localities } = pincodeSDetails.data;
      setSLocalityOptions(localities);
      setFormValues({ ...formValues, s_city: city_district, s_state: state });
    }
  }, [fetchingSPincode]);

  useEffect(() => {
    if (fetchingDPincodeError) {
      setErrors({ ...errors, d_pincode: "Invalid pincode" });
      setDLocalityOptions(prevState => []);
    } else setErrors({ ...errors, d_pincode: undefined });
    if (
      pincodeDDetails?.data &&
      Object.keys(pincodeDDetails?.data).length > 0
    ) {
      const { city_district, state, localities } = pincodeDDetails.data;
      setDLocalityOptions(localities);
      setFormValues({ ...formValues, d_city: city_district, d_state: state });
    }
  }, [fetchingDPincode]);

  useEffect(() => {
    if (vendorStoreData?.success && useDefaultOrderValidationSettings) {
      const { minOrderQty, minOrderValue, validationCondition } =
        vendorStoreData.data.store.orderValidation;

      setFormValues(prev => ({
        ...prev,
        orderValidationSettings: {
          validationType: "COMPANY_SETTINGS",
          minOrderQty: minOrderQty || "",
          minOrderValue: minOrderValue || "",
          validationCondition: validationCondition || "",
        },
      }));
    }
  }, [vendorStoreData, useDefaultOrderValidationSettings]);

  // Function to handle pincode change and check for 6 digits
  const handlePincodeChange = event => {
    const { name, value } = event.target;
    if (/^\d{0,6}$/.test(value)) {
      // Accept only numbers, up to 6 digits
      setFormValues({ ...formValues, [name]: value });
      if (value.length === 6) {
        // fetchPincodeData(value); // Trigger API call when 6 digits are entered
        if (name == "s_pincode") triggerSFetchPincodeQuery({ pincode: value });
        else triggerDFetchPincodeQuery({ pincode: value });
      }
    }
  };

  const handleChange = useCallback(e => {
    const { name, value } = e.target;

    // Add phone number validation for both phone numbers
    if (name === "altPhoneNumber" || name === "regPhoneNumber") {
      if (/^\d{0,10}$/.test(value)) {
        // Only allow up to 10 digits
        setFormValues(prevFormValues => ({
          ...prevFormValues,
          [name]: value,
        }));
      }
      return;
    }

    if (
      name === "validationCondition" ||
      name === "minOrderQty" ||
      name === "minOrderValue"
    ) {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        orderValidationSettings: {
          ...prevFormValues.orderValidationSettings,
          [name]: value,
        },
      }));
      return;
    }
    console.log("handle change called");

    const sDAddressFields = {
      s_pincode: "d_pincode",
      s_localAddress: "d_localAddress",
      s_locality: "d_locality",
      s_city: "d_city",
      s_state: "d_state",
    };
    if (sameAsSAddressCheckbox && Object.keys(sDAddressFields).includes(name)) {
      console.log("fn run");
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        [name]: value,
        [sDAddressFields[name]]: value,
      }));
    } else {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        [name]: value,
      }));
    }
  }, []);

  const handleDeliveryAddressCheckbox = () => {
    if (sameAsSAddressCheckbox) {
      setSameAsSAddressCheckbox(false);
      setFormValues(prevState => ({
        ...prevState,
        d_pincode: "",
        d_locality: "",
        d_localAddress: "",
        d_city: "",
        d_state: "",
      }));
    } else {
      setSameAsSAddressCheckbox(true);
      setFormValues(prevState => ({
        ...prevState,
        d_pincode: formValues.s_pincode,
        d_locality: formValues.s_locality,
        d_localAddress: formValues.s_localAddress,
        d_city: formValues.s_city,
        d_state: formValues.s_state,
      }));
    }
  };

  // Handle select all brands checkbox
  const handleSelectAllBrands = event => {
    const checked = event.target.checked;
    setSelectAllBrands(checked);

    if (checked) {
      // If all brands selected
      const allBrandIds =
        brandsResponseData?.data?.brands?.map(brand => brand._id) || [];
      setSelectedBrands(allBrandIds);
      setFormValues(prev => ({
        ...prev,
        brandVisibility: {
          visibilityType: "ALL_COMPANY_BRANDS",
        },
      }));
    } else {
      // If unchecked and all brands were selected
      setFormValues(prev => ({
        ...prev,
        brandVisibility: {
          visibilityType: "USER_SPECIFIC",
          removedBrands: [],
        },
      }));
    }
  };

  // Handle individual brand selection
  const handleBrandSelection = brandId => {
    const allBrands = brandsResponseData?.data?.brands || [];

    setSelectedBrands(prev => {
      let newSelection;

      if (prev.includes(brandId)) {
        // Uncheck a brand
        newSelection = prev.filter(id => id !== brandId);

        setFormValues(prev => ({
          ...prev,
          brandVisibility: {
            visibilityType: "USER_SPECIFIC",
            removedBrands: allBrands
              .map(brand => brand._id)
              .filter(id => !newSelection.includes(id)),
          },
        }));

        setSelectAllBrands(false);
      } else {
        // Check a brand
        newSelection = [...prev, brandId];

        setFormValues(prev => ({
          ...prev,
          brandVisibility: {
            visibilityType: "USER_SPECIFIC",
            removedBrands: allBrands
              .map(brand => brand._id)
              .filter(id => !newSelection.includes(id)),
          },
        }));

        // If all brands are now selected, update accordingly
        if (newSelection.length === allBrands.length) {
          setSelectAllBrands(true);
          setFormValues(prev => ({
            ...prev,
            brandVisibility: {
              visibilityType: "ALL_COMPANY_BRANDS",
            },
          }));
        }
      }
      return newSelection;
    });
  };

  const handleBrandFilter = filterType => {
    setBrandFilter(filterType);
  };

  const getFilteredBrands = () => {
    if (!brandsResponseData?.success) return [];

    const allBrands = brandsResponseData.data.brands;

    // First filter by search query
    const searchFiltered = searchQuery
      ? allBrands.filter(brand =>
          brand.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : allBrands;

    // Then apply the view filter (ALL/SELECTED/UNSELECTED)
    const filteredBrands =
      {
        SELECTED: searchFiltered.filter(brand =>
          selectedBrands.includes(brand._id),
        ),
        UNSELECTED: searchFiltered.filter(
          brand => !selectedBrands.includes(brand._id),
        ),
        ALL: searchFiltered,
      }[brandFilter] || searchFiltered;

    return {
      brands: filteredBrands,
      isEmpty: filteredBrands.length === 0,
    };
  };

  // Add handler for unselect all
  const handleUnselectAll = () => {
    setSelectedBrands([]);
    setSelectAllBrands(false);
    setFormValues(prev => ({
      ...prev,
      brandVisibility: {
        visibilityType: "USER_SPECIFIC",
        removedBrands:
          brandsResponseData?.data?.brands?.map(brand => brand._id) || [],
      },
    }));
  };

  // Add handler for search input
  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  // Add clear search handler
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Update handleSubmit function
  const handleSubmit = async () => {
    const errors = validateForm(
      formValues,
      selectAllBrands,
      selectedBrands,
      files,
    );
    if (Object.keys(errors).length > 0) {
      errorToast("Error in form validation");
      setErrors(errors);
      return;
    }

    try {
      // Prepare customer data
      const customerData = {
        businessName: formValues.firmName,
        ownerName: formValues.ownerName,
        billPrintName: formValues.billPrintName,
        registeredPhoneNo: formValues.regPhoneNumber,
        alternatePhoneNo: formValues.altPhoneNumber,
        storeType:
          formValues.storeType == "Wholesaler" ? "WHOLESALER" : "RETAILER",
        shopAddress: {
          pincode: formValues.s_pincode,
          localAddress: formValues.s_localAddress,
          locality: formValues.s_locality,
          city_district: formValues.s_city,
          state: formValues.s_state,
        },
        deliveryAddress: {
          pincode: formValues.d_pincode,
          localAddress: formValues.d_localAddress,
          locality: formValues.d_locality,
          city_district: formValues.d_city,
          state: formValues.d_state,
        },
        GSTIN: formValues.gstin,
        dealerType:
          formValues.taxPayerType == "Regular" ? "REGULAR" : "COMPOSITION",
        vcConfigSettings: {
          creditLimit: Number(formValues.creditLimit),
          orderValidation: {
            validationType: useDefaultOrderValidationSettings
              ? "COMPANY_SETTINGS"
              : "USER_SPECIFIC",
            ...(formValues.orderValidationSettings.validationType ===
            "USER_SPECIFIC"
              ? formValues.orderValidationSettings
              : {}),
          },
          brandVisibility: formValues.brandVisibility,
        },
        additionalNotes: formValues.additionalNotes,
      };

      // Create customer
      const response = await createCustomer(customerData).unwrap();

      // If customer creation successful and files exist, upload documents
      if (response.success && (files.gstCertificate || files.businessPan)) {
        const formData = new FormData();
        if (files.gstCertificate) {
          formData.append("gstCertificate", files.gstCertificate);
        }
        if (files.businessPan) {
          formData.append("businessPan", files.businessPan);
        }

        await uploadDocuments({
          customerId: response.data.savedStore._id,
          formData,
        }).unwrap();
      }

      successToast("Customer created successfully!");

      // Reset all states
      setFormValues(initialFormState);
      setFiles({ gstCertificate: null, businessPan: null });
      setErrors({});
      setSelectedBrands([]);
      setSelectAllBrands(false);
      setUseDefaultOrderValidationSettings(false);
      setSameAsSAddressCheckbox(false);
      setBrandFilter("ALL");
      setSearchQuery("");
      setSLocalityOptions([]);
      setDLocalityOptions([]);
    } catch (error) {
      errorToast(error.data?.error?.message || "Failed to create customer");
      console.error("Error creating customer:", error);
    }
  };

  // 1. Memoize the isFormModified function
  const isFormModified = useMemo(() => {
    return (
      JSON.stringify(formValues) !== JSON.stringify(initialFormState) ||
      files.gstCertificate ||
      files.businessPan ||
      selectedBrands.length > 0
    );
  }, [
    formValues,
    files.gstCertificate,
    files.businessPan,
    selectedBrands.length,
  ]);

  // 2. Add debounce for pincode API calls
  const debouncedPincodeQuery = useCallback(
    debounce((name, value) => {
      if (name === "s_pincode") {
        triggerSFetchPincodeQuery({ pincode: value });
      } else {
        triggerDFetchPincodeQuery({ pincode: value });
      }
    }, 500),
    [],
  );

  // 3. Add cleanup for effects
  useEffect(() => {
    return () => {
      setFormValues(initialFormState);
      setErrors({});
      setSelectedBrands([]);
    };
  }, []);

  // 4. Add proper error handling for API failures
  useEffect(() => {
    if (isFetchBrandsError) {
      // Show error toast/notification instead of console.log
      console.log(fetchBrandsError, "error fetching");
    }
  }, [isFetchBrandsError, fetchBrandsError]);

  // Add handler for default settings checkbox
  const handleDefaultSettingsChange = event => {
    const checked = event.target.checked;
    setUseDefaultOrderValidationSettings(checked);

    if (checked) {
      triggerFetchVendorStore();
    } else {
      // Reset to empty values when unchecking, but maintain controlled state
      setFormValues(prev => ({
        ...prev,
        orderValidationSettings: {
          validationType: "USER_SPECIFIC",
          minOrderQty: "",
          minOrderValue: "",
          validationCondition: "",
        },
      }));
    }
  };

  const handleFileChange = (event, fileType) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [fileType]: "Invalid file type. Only PDF, JPG, JPEG, PNG are allowed",
        }));
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({
          ...prev,
          [fileType]: "File size should not exceed 5MB",
        }));
        return;
      }

      // Clear any existing errors for this file type
      setErrors(prev => ({
        ...prev,
        [fileType]: undefined,
      }));

      // Set the file if validation passes
      setFiles(prev => ({
        ...prev,
        [fileType]: file,
      }));
    }
  };

  const handleFileDelete = fileType => {
    setFiles(prev => ({
      ...prev,
      [fileType]: null,
    }));
  };

  const handleCancelClick = () => {
    // If form has been modified, show confirmation dialog
    if (
      JSON.stringify(formValues) !== JSON.stringify(initialFormState) ||
      files.gstCertificate ||
      files.businessPan ||
      selectedBrands.length > 0
    ) {
      setShowCancelDialog(true);
    } else {
      // If no changes, just reset
      handleConfirmCancel();
    }
  };

  const handleConfirmCancel = () => {
    // Reset all states
    setFormValues(initialFormState);
    setFiles({ gstCertificate: null, businessPan: null });
    setErrors({});
    setSelectedBrands([]);
    setSelectAllBrands(false);
    setUseDefaultOrderValidationSettings(false);
    setSameAsSAddressCheckbox(false);
    setBrandFilter("ALL");
    setSearchQuery("");
    setSLocalityOptions([]);
    setDLocalityOptions([]);
    setShowCancelDialog(false);
  };

  // 5. Memoize the cancel button to prevent unnecessary re-renders
  const CancelButton = useMemo(
    () => (
      <button
        onClick={handleCancelClick}
        disabled={isCreatingCustomer || isUploadingDocuments}
        className={`px-6 py-2.5 rounded-lg text-base font-medium transition-all ${
          isFormModified
            ? "border-2 border-[#5652B0] text-[#5652B0] hover:bg-[#5652B0] hover:text-white"
            : "border-2 border-gray-200 text-gray-400 cursor-not-allowed"
        } ${
          (isCreatingCustomer || isUploadingDocuments) &&
          "opacity-50 cursor-not-allowed"
        }`}>
        Cancel
      </button>
    ),
    [
      isFormModified,
      isCreatingCustomer,
      isUploadingDocuments,
      handleCancelClick,
    ],
  );

  // 6. Memoize the dialog component
  const CancelDialog = useMemo(
    () => (
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description">
        <DialogTitle id="cancel-dialog-title">{"Confirm Cancel"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel? All unsaved changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setShowCancelDialog(false)}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md">
            No, Keep Editing
          </button>
          <button
            onClick={handleConfirmCancel}
            className="px-4 py-2 bg-red-500 text-white hover:bg-red-600 rounded-md ml-2">
            Yes, Cancel
          </button>
        </DialogActions>
      </Dialog>
    ),
    [showCancelDialog, handleConfirmCancel],
  );

  return (
    <div className="ml-[2.083333vw] mr-[2.638888vw] bg-white mt-[4.8vh] h-[84vh] overflow-y-auto">
      <div className="bg-white rounded-xl pb-4 shadow-sm">
        <div className="flex items-center justify-between px-6 h-[8.7vh] border-b">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Add Customer
          </h1>
        </div>
        <div className="grid grid-cols-3 gap-6 w-[90%] mt-6 px-6">
          <MemoizedTextField
            id="outlined-basic"
            label="Firm Name"
            name="firmName"
            variant="outlined"
            className="border-dotted border-2 border-green-400"
            required={true}
            size="small"
            inputProps={{ style: { textTransform: "capitalize" } }}
            value={formValues.firmName}
            onChange={handleChange}
            error={errors?.firmName ? true : undefined}
            helperText={errors?.firmName}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="Owner Name"
            name="ownerName"
            variant="outlined"
            required={true}
            size="small"
            inputProps={{ style: { textTransform: "capitalize" } }}
            value={formValues.ownerName}
            onChange={handleChange}
            error={errors?.ownerName ? true : undefined}
            helperText={errors?.ownerName}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="Bill Print Name"
            name="billPrintName"
            variant="outlined"
            required={true}
            size="small"
            inputProps={{ style: { textTransform: "capitalize" } }}
            value={formValues.billPrintName}
            onChange={handleChange}
            error={errors?.billPrintName ? true : undefined}
            helperText={errors?.billPrintName}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="Registration Phone Number"
            name="regPhoneNumber"
            variant="outlined"
            required={true}
            size="small"
            value={formValues.regPhoneNumber}
            onChange={handleChange}
            error={errors?.regPhoneNumber ? true : undefined}
            helperText={errors?.regPhoneNumber}
            inputProps={{ maxLength: 10 }}
            inputMode="numeric"
          />
          <MemoizedTextField
            id="outlined-basic"
            label="Alternate Phone Number"
            name="altPhoneNumber"
            variant="outlined"
            size="small"
            value={formValues.altPhoneNumber}
            onChange={handleChange}
            error={errors?.altPhoneNumber ? true : undefined}
            helperText={errors?.altPhoneNumber}
            inputProps={{ maxLength: 10 }}
            inputMode="numeric"
          />
          <MemoizedTextField
            id="outlined-basic"
            select
            size="small"
            name="storeType"
            variant="outlined"
            required={true}
            label="Store Type"
            value={formValues.storeType}
            onChange={handleChange}
            error={errors?.storeType ? true : undefined}
            helperText={errors?.storeType}>
            {storeTypeOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </MemoizedTextField>
        </div>
        <ProductSection data={"Shop Address*"} />
        <div className="grid grid-cols-4 gap-x-3 gap-y-3 w-[90%] h-[100%] mt-[1.959259259vh] box-border px-[3.220338983%]">
          <MemoizedTextField
            id="outlined-basic-s_pincode"
            label="Pincode"
            name="s_pincode"
            variant="outlined"
            disabled={fetchingSPincode}
            className="border-dotted border-2 border-green-400"
            required={true}
            size="small"
            onChange={handlePincodeChange}
            value={formValues.s_pincode}
            error={errors?.s_pincode ? true : undefined}
            helperText={errors?.s_pincode}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {fetchingSPincode && <CircularProgress size={20} />}
                </InputAdornment>
              ),
            }}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="Local Address"
            name="s_localAddress"
            variant="outlined"
            className="border-dotted border-2 border-green-400"
            required={true}
            size="small"
            inputProps={{ style: { textTransform: "capitalize" } }}
            value={formValues.s_localAddress}
            onChange={handleChange}
            error={errors?.s_localAddress ? true : undefined}
            helperText={errors?.s_localAddress}
          />
          <Autocomplete
            id="combo-box-demo"
            freeSolo
            value={formValues.s_locality}
            size="small"
            options={sLocalityOptions}
            sx={{ width: 220 }}
            onChange={(_, value) => {
              handleChange({ target: { name: "s_locality", value } });
            }}
            renderInput={params => (
              <MemoizedTextField
                {...params}
                id="outlined-basic"
                label="Locality"
                name="s_locality"
                variant="outlined"
                required={true}
                size="small"
                value={formValues.s_locality}
                onChange={handleChange}
                error={errors?.s_locality ? true : undefined}
                helperText={errors?.s_locality}
              />
            )}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="City/District"
            name="s_city"
            variant="outlined"
            required={true}
            disabled={true}
            size="small"
            value={formValues.s_city}
            onChange={handleChange}
            error={errors?.s_city ? true : undefined}
            helperText={errors?.s_city}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="State"
            name="s_state"
            variant="outlined"
            required={true}
            disabled={true}
            size="small"
            value={formValues.s_state}
            onChange={handleChange}
            error={errors?.s_state ? true : undefined}
            helperText={errors?.s_state}
          />
        </div>
        <ProductSection data={"Delivery Address*"} />
        <div className="w-[50%] flex items-center mx-[2.52222vw] mt-3 justify-start flex-row">
          <Checkbox
            sx={{
              color: "#5652B0",
              "&.Mui-checked": {
                color: "#5652B0",
              },
            }}
            value={sameAsSAddressCheckbox}
            onChange={handleDeliveryAddressCheckbox}
          />{" "}
          <p className="font-outfit text-[18px] font-medium">
            Same as Shop Address
          </p>
        </div>
        <div className="grid grid-cols-4 gap-x-3 gap-y-3 w-[90%] h-[100%] mt-[1.959259259vh] box-border px-[3.220338983%]">
          <MemoizedTextField
            id="outlined-basic-d_pincode"
            label="Pincode"
            name="d_pincode"
            variant="outlined"
            className="border-dotted border-2 border-green-400"
            required={true}
            disabled={sameAsSAddressCheckbox}
            size="small"
            value={formValues.d_pincode}
            onChange={handlePincodeChange}
            error={errors?.d_pincode ? true : undefined}
            helperText={errors?.d_pincode}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {fetchingDPincode && <CircularProgress size={20} />}
                </InputAdornment>
              ),
            }}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="Local Address"
            name="d_localAddress"
            variant="outlined"
            className="border-dotted border-2 border-green-400"
            required={true}
            disabled={sameAsSAddressCheckbox}
            size="small"
            inputProps={{ style: { textTransform: "capitalize" } }}
            value={formValues.d_localAddress}
            onChange={handleChange}
            error={errors?.d_localAddress ? true : undefined}
            helperText={errors?.d_localAddress}
          />
          <Autocomplete
            id="combo-box-demo"
            freeSolo
            value={formValues.d_locality}
            size="small"
            disabled={sameAsSAddressCheckbox}
            options={dLocalityOptions}
            sx={{ width: 220 }}
            onChange={(_, value) => {
              handleChange({ target: { name: "d_locality", value } });
            }}
            renderInput={params => (
              <MemoizedTextField
                {...params}
                id="outlined-basic"
                label="Locality"
                name="d_locality"
                variant="outlined"
                required={true}
                size="small"
                value={formValues.d_locality}
                onChange={handleChange}
                error={errors?.d_locality ? true : undefined}
                helperText={errors?.d_locality}
              />
            )}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="City/District"
            name="d_city"
            variant="outlined"
            required={true}
            disabled={true}
            size="small"
            value={formValues.d_city}
            onChange={handleChange}
            error={errors?.d_city ? true : undefined}
            helperText={errors?.d_city}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="State"
            name="d_state"
            variant="outlined"
            required={true}
            disabled={true}
            size="small"
            value={formValues.d_state}
            onChange={handleChange}
            error={errors?.d_state ? true : undefined}
            helperText={errors?.d_state}
          />
        </div>
        <ProductSection data={"Business Details"} />
        <div className="grid grid-cols-4 gap-x-3 gap-y-3 w-[90%] h-[100%] mt-[1.959259259vh] box-border px-[3.220338983%]">
          <MemoizedTextField
            id="outlined-basic"
            label="GSTIN"
            name="gstin"
            required={true}
            variant="outlined"
            className="border-dotted border-2 border-green-400"
            size="small"
            value={formValues.gstin}
            onChange={handleChange}
            error={errors?.gstin ? true : undefined}
            helperText={errors?.gstin}
            inputProps={{ style: { textTransform: "uppercase" } }}
          />
          <MemoizedTextField
            id="outlined-basic"
            select
            size="small"
            name="taxPayerType"
            variant="outlined"
            required={true}
            label="Tax Payer Type"
            value={formValues.taxPayerType}
            onChange={handleChange}
            error={errors?.taxPayerType ? true : undefined}
            helperText={errors?.taxPayerType}>
            {taxPayerTypeoptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </MemoizedTextField>
        </div>
        <ProductSection data={"Documents Upload"} />
        <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
          <div className="space-y-6">
            {/* GST Certificate */}
            <div className="flex items-start gap-x-8">
              <p className="font-outfit text-base font-medium w-[150px] mt-2 text-gray-700">
                GST Certificate:
              </p>
              <div className="flex flex-col gap-y-2">
                {files.gstCertificate ? (
                  <div className="flex items-start gap-x-4">
                    {getFilePreview(files.gstCertificate)}
                    <div className="flex flex-col gap-y-2">
                      <p className="font-outfit text-sm text-gray-600 break-all max-w-[200px]">
                        {files.gstCertificate.name}
                      </p>
                      <button
                        onClick={() => handleFileDelete("gstCertificate")}
                        className="px-4 py-1.5 rounded-lg text-red-500 border border-red-500 hover:bg-red-50 transition-all text-sm font-medium">
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="file"
                      id="gstCertificate"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => handleFileChange(e, "gstCertificate")}
                      className="hidden"
                    />
                    <label
                      htmlFor="gstCertificate"
                      className="flex items-center gap-x-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-300 hover:bg-gray-100 transition-all cursor-pointer text-sm font-medium">
                      <Upload size={16} />
                      Choose File
                    </label>
                    {errors?.gstCertificate && (
                      <p className="absolute text-red-500 text-xs mt-1">
                        {errors.gstCertificate}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Business PAN */}
            <div className="flex items-start gap-x-8 mt-6">
              <p className="font-outfit text-[16px] w-[150px] mt-2">
                Business PAN:
              </p>
              <div className="flex flex-col gap-y-2">
                {files.businessPan ? (
                  <div className="flex items-start gap-x-4">
                    {getFilePreview(files.businessPan)}
                    <div className="flex flex-col gap-y-2">
                      <p className="font-outfit text-[14px] text-gray-600 break-all max-w-[200px]">
                        {files.businessPan.name}
                      </p>
                      <button
                        onClick={() => handleFileDelete("businessPan")}
                        className="px-4 py-1.5 rounded-md text-red-500 border border-red-500 hover:bg-red-50 transition-colors text-sm font-outfit w-fit">
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="file"
                      id="businessPan"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => handleFileChange(e, "businessPan")}
                      className="hidden"
                    />
                    <label
                      htmlFor="businessPan"
                      className="flex items-center gap-x-2 px-4 py-1.5 rounded-md bg-gray-50 border border-gray-300 hover:bg-gray-100 transition-colors cursor-pointer text-sm font-outfit">
                      <Upload size={16} />
                      Choose File
                    </label>
                    {errors?.businessPan && (
                      <p className="absolute text-red-500 text-xs mt-1">
                        {errors.businessPan}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* File type hint */}
            <p className="text-gray-500 text-xs mt-4 ml-[150px]">
              Accepted file types: PDF, JPG, JPEG, PNG (Max size: 5MB)
            </p>
          </div>
        </div>
        <ProductSection data={"Order Conditions"} />
        <div className="flex items-center justify-start mx-[2.2vw]">
          <MemoizedTextField
            id="outlined-basic"
            label="Credit Limit"
            name="creditLimit"
            variant="outlined"
            type="number"
            required={true}
            size="small"
            value={formValues.creditLimit}
            onChange={handleChange}
            error={errors?.creditLimit ? true : undefined}
            helperText={errors?.creditLimit}
          />
        </div>
        <div className="w-[50%] flex items-center mx-[2.52222vw] mt-3 justify-start flex-row">
          <Checkbox
            sx={{
              color: "#5652B0",
              "&.Mui-checked": {
                color: "#5652B0",
              },
            }}
            checked={useDefaultOrderValidationSettings}
            onChange={handleDefaultSettingsChange}
          />{" "}
          <p className="font-outfit text-[18px] font-medium">
            MOQ/MOV same as company's default settings
          </p>
        </div>
        <div className="grid grid-cols-4 gap-x-3 gap-y-3 w-[90%] h-[100%] mt-[1.959259259vh] box-border px-[3.220338983%]">
          <MemoizedTextField
            id="outlined-basic"
            label="Min. Order Quantity (MOQ)"
            name="minOrderQty"
            required={true}
            variant="outlined"
            type="number"
            inputProps={{
              min: 0,
              style: {
                WebkitTextFillColor: useDefaultOrderValidationSettings
                  ? "#000000"
                  : "inherit",
                opacity: 1,
              },
            }}
            className="border-dotted border-2 border-green-400"
            size="small"
            value={formValues.orderValidationSettings.minOrderQty}
            onChange={handleChange}
            error={errors?.minOrderQty ? true : undefined}
            helperText={errors?.minOrderQty}
            disabled={useDefaultOrderValidationSettings}
            InputProps={{
              endAdornment: fetchingVendorStore &&
                useDefaultOrderValidationSettings && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
            }}
          />
          <MemoizedTextField
            id="outlined-basic"
            label="Min. Order Value (MOV)"
            name="minOrderValue"
            required={true}
            variant="outlined"
            type="number"
            inputProps={{
              min: 0,
              style: {
                WebkitTextFillColor: useDefaultOrderValidationSettings
                  ? "#000000"
                  : "inherit",
                opacity: 1,
              },
            }}
            className="border-dotted border-2 border-green-400"
            size="small"
            value={formValues.orderValidationSettings.minOrderValue}
            onChange={handleChange}
            error={errors?.minOrderValue ? true : undefined}
            helperText={errors?.minOrderValue}
            disabled={useDefaultOrderValidationSettings}
            InputProps={{
              endAdornment: fetchingVendorStore &&
                useDefaultOrderValidationSettings && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
            }}
          />
          <div>
            <FormLabel id="demo-row-radio-buttons-group-label">
              MOQ/MOV Condition
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="validationCondition"
              value={
                formValues.orderValidationSettings.validationCondition || ""
              }
              onChange={handleChange}
              error={Boolean(errors?.moqMovCondition)}>
              <FormControlLabel
                value="BOTH"
                control={
                  <Radio
                    disableRipple
                    sx={{
                      color: errors?.moqMovCondition ? "#d32f2f" : "#5652B0",
                      "&.Mui-checked": {
                        color: errors?.moqMovCondition ? "#d32f2f" : "#5652B0",
                      },
                    }}
                  />
                }
                label="Both"
                disabled={useDefaultOrderValidationSettings}
              />
              <FormControlLabel
                value="ANY_ONE"
                control={
                  <Radio
                    disableRipple
                    sx={{
                      color: errors?.moqMovCondition ? "#d32f2f" : "#5652B0",
                      "&.Mui-checked": {
                        color: errors?.moqMovCondition ? "#d32f2f" : "#5652B0",
                      },
                    }}
                  />
                }
                label="Any One"
                disabled={useDefaultOrderValidationSettings}
              />
            </RadioGroup>
            {errors?.moqMovCondition && (
              <FormHelperText error>{errors.moqMovCondition}</FormHelperText>
            )}
          </div>
        </div>

        <ProductSection data={"Brand Visibility"} />
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-x-4">
              <div className="flex items-center">
                <Checkbox
                  sx={{
                    color: "#5652B0",
                    "&.Mui-checked": {
                      color: "#5652B0",
                    },
                  }}
                  checked={selectAllBrands}
                  onChange={handleSelectAllBrands}
                />
                <p className="font-outfit text-lg font-medium text-gray-800">
                  Select all brands
                </p>
              </div>
              {selectedBrands.length > 0 && (
                <button
                  onClick={handleUnselectAll}
                  className="border border-[#5652B0] text-[#5652B0] px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-[#5652B0] hover:text-white transition-all">
                  Unselect All
                </button>
              )}
            </div>

            <div className="flex items-center gap-x-6">
              <div className="flex gap-x-4">
                {["ALL", "SELECTED", "UNSELECTED"].map(filter => (
                  <button
                    key={filter}
                    onClick={() => handleBrandFilter(filter)}
                    className={`font-medium text-sm transition-colors ${
                      brandFilter === filter
                        ? "text-[#5652B0] underline"
                        : "text-gray-600 hover:text-[#5652B0]"
                    }`}>
                    View {filter.toLowerCase()}
                  </button>
                ))}
              </div>

              <div className="relative">
                <SearchIcon
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  size={18}
                  color="#83899F"
                />
                <input
                  type="text"
                  placeholder="Search by Name..."
                  className="w-[300px] h-10 pl-10 pr-8 rounded-lg border border-gray-200 focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] outline-none transition-all"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors">
                    <CircleX size={16} color="#83899F" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-4 max-h-[400px] overflow-y-auto p-4 bg-gray-50 rounded-lg">
            {fetchBrandsLoading && <CircularProgress size={40} />}
            {isFetchBrandsError && <div>error fetching brands</div>}
            {brandsResponseData?.success && (
              <>
                {getFilteredBrands().isEmpty ? (
                  <div className="col-span-5 flex items-center justify-center h-full">
                    <p className="text-gray-500 text-lg font-outfit">
                      {searchQuery
                        ? `No brands found matching "${searchQuery}"`
                        : brandFilter === "SELECTED"
                        ? "No selected brands"
                        : brandFilter === "UNSELECTED"
                        ? "No unselected brands"
                        : "No brands available"}
                    </p>
                  </div>
                ) : (
                  getFilteredBrands().brands.map(brand => (
                    <BrandCheckbox
                      key={brand._id}
                      id={brand._id}
                      label={brand.name}
                      checked={selectedBrands.includes(brand._id)}
                      onChange={() => handleBrandSelection(brand._id)}
                    />
                  ))
                )}
                {errors?.brandVisibility && (
                  <div className="col-span-5 mt-2">
                    <FormHelperText error>
                      {errors.brandVisibility}
                    </FormHelperText>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <ProductSection data={"Additional Notes"} />
        <div className="w-[90%] mt-[1.959259259vh] box-border px-[3.220338983%] mb-6">
          <MemoizedTextField
            id="outlined-multiline-static"
            label="Notes"
            name="additionalNotes"
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            placeholder="Add any additional information about the customer..."
            value={formValues.additionalNotes}
            onChange={handleChange}
            inputProps={{
              maxLength: 500,
              style: { resize: "none" },
            }}
            helperText={`${formValues.additionalNotes.length}/500 characters`}
          />
        </div>
        <div className="flex items-center justify-end gap-x-4 px-6 py-8 border-t mt-8">
          <button
            onClick={handleCancelClick}
            disabled={isCreatingCustomer || isUploadingDocuments}
            className={`px-6 py-2.5 rounded-lg text-base font-medium transition-all ${
              isFormModified
                ? "border-2 border-[#5652B0] text-[#5652B0] hover:bg-[#5652B0] hover:text-white"
                : "border-2 border-gray-200 text-gray-400 cursor-not-allowed"
            } ${
              (isCreatingCustomer || isUploadingDocuments) &&
              "opacity-50 cursor-not-allowed"
            }`}>
            Cancel
          </button>

          <button
            className={`px-6 py-2.5 rounded-lg text-base font-medium text-white transition-all ${
              isCreatingCustomer || isUploadingDocuments
                ? "bg-purple-500"
                : "bg-[#5652B0] hover:bg-[#4A47A3]"
            }`}
            onClick={handleSubmit}
            disabled={isCreatingCustomer || isUploadingDocuments}>
            {isCreatingCustomer || isUploadingDocuments ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
      {CancelDialog}
    </div>
  );
};

export default AddCustomer;
